import Editor from "@monaco-editor/react";
import {
  AccountTree as AccountTreeIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { theme } from "@/theme/theme";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { usePreSignedUrl } from "../hooks/use-pre-signed-url";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { Block, Test, TestContext, TestRunStep } from "../types.ts";
import { getExistingGroupNames, getGroupMembers, getStepGroupMetadata } from "../utils/group-utils";
import { clickableProps } from "../utils/keyboard";
import { humanizeText } from "../utils/text.ts";
import { getRunStatusColor } from "../utils.ts";
import { BlockEditor } from "./block-editor";

interface TestDataTabs {
  dataTab?: "main" | "data";
}

interface TestContextQuery {
  debug?: string;
}

interface TestContextDetailsProps {
  testContext: TestContext;
  testContextStep: TestRunStep | null;
  allContexts?: TestContext[];
  onUpdate?: (updatedCtx: TestContext) => void;
}

const ImageFromPath = (props: { path: string; height?: string | number }) => {
  const { path } = props;

  const { data, isLoading } = usePreSignedUrl(path);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!data) {
    return <Typography>Image not found</Typography>;
  }

  return (
    <img
      src={data.url ?? "https://placehold.co/600x400"}
      alt="Screenshot preview"
      style={{
        display: "block",
        width: "auto",
        height: props.height ?? "300px",
        top: "0px",
        left: "0px",
        pointerEvents: "none",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    />
  );
};

export const TestContextDetails: React.FC<TestContextDetailsProps> = ({
  testContext,
  testContextStep,
  allContexts,
  onUpdate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { testId } = useParams();

  // Fetch the test to resolve its app_config_id for app-config-scoped pickers (e.g. CheckSelect).
  const { data: test } = useApiQuery<Test>(`/tests/${testId}`, {
    enabled: !!testId,
  });

  const { mutate: updateContext, isPending: isUpdating } = useApiMutation<
    Partial<TestContext>,
    TestContext
  >("PUT", `/tests/${testId}/context/${testContext?.id}`, {
    onSuccess: (updatedCtx) => {
      setIsEditingName(false);
      setIsEditingDescription(false);
      setIsEditingEndGoal(false);
      enqueueSnackbar("Test context updated successfully", {
        variant: "success",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      onUpdate?.(updatedCtx);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update test context: ${error.message}`, {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingEndGoal, setIsEditingEndGoal] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedEndGoal, setEditedEndGoal] = useState("");
  const [editedGroup, setEditedGroup] = useState("");
  const [jsonData, setJsonData] = useState("{}");
  const [isJsonModified, setIsJsonModified] = useState(false);

  const { params: dataTabParams, setParam: setDataTabParam } = useTypedQueryParams<TestDataTabs>();
  const dataTab = dataTabParams.dataTab ?? "main";

  const { params: debugParams, setParam: setDebugParam } = useTypedQueryParams<TestContextQuery>();
  const useBlockEditor = debugParams.debug !== "true";

  const handleDataChange = (_: React.SyntheticEvent, newValue: string) => {
    setDataTabParam("dataTab", newValue as TestDataTabs["dataTab"]);
  };

  // Get existing group names for autocomplete
  const existingGroups = useMemo(() => {
    if (!allContexts) return [];
    return getExistingGroupNames(allContexts);
  }, [allContexts]);

  // Determine if current step is a group check
  const isGroupCheck = useMemo(() => {
    if (!testContext?.data?.group || !allContexts) return false;
    const metadata = getStepGroupMetadata(testContext, allContexts);
    return metadata.isGroupCheck;
  }, [testContext, allContexts]);

  // Helper functions for editing
  const startEditing = (field: "name" | "description" | "endGoal") => {
    if (testContext) {
      if (field === "name") {
        setEditedName(testContext.name);
        setIsEditingName(true);
      } else if (field === "description") {
        setEditedDescription(testContext.description || "");
        setIsEditingDescription(true);
      } else if (field === "endGoal") {
        setEditedEndGoal(testContext.end_goal || "");
        setIsEditingEndGoal(true);
      }
    }
  };

  const cancelEditing = (field: "name" | "description" | "endGoal") => {
    if (field === "name") {
      setIsEditingName(false);
    } else if (field === "description") {
      setIsEditingDescription(false);
    } else if (field === "endGoal") {
      setIsEditingEndGoal(false);
    }
  };

  const saveField = (field: "name" | "description" | "endGoal") => {
    if (!testContext) return;

    let updateData: Partial<TestContext> = {};

    if (field === "name") {
      updateData = { name: editedName };
    } else if (field === "description") {
      updateData = { description: editedDescription };
    } else if (field === "endGoal") {
      updateData = { end_goal: editedEndGoal };
    }

    updateContext(updateData);
  };

  const saveGroup = async () => {
    if (!testContext?.data) return;

    const oldGroup = testContext.data.group;
    const newGroup = editedGroup.trim() || undefined;

    // If this is a group check and we're changing the name
    if (isGroupCheck && oldGroup && oldGroup !== newGroup && allContexts) {
      // Update all steps in the group
      const groupMembers = getGroupMembers(oldGroup, allContexts);

      // Show confirmation
      const confirmed = window.confirm(
        `This will rename the group for all ${groupMembers.length} steps. Continue?`,
      );

      if (!confirmed) {
        setIsEditingGroup(false);
        return;
      }

      // Sequential updates for all group members
      try {
        for (const member of groupMembers) {
          // Skip if member has no data
          if (!member.data) continue;

          await new Promise<void>((resolve, reject) => {
            updateContext(
              {
                data: {
                  ...member.data,
                  group: newGroup,
                } as Block,
              },
              {
                onSuccess: () => resolve(),
                onError: (error) => reject(error),
              },
            );
          });
        }
        enqueueSnackbar(`Updated group for ${groupMembers.length} steps`, {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      } catch (error) {
        enqueueSnackbar(`Failed to update all steps: ${error}`, {
          variant: "error",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      }
    } else {
      // Single update
      if (!testContext.data) return;

      updateContext({
        data: {
          ...testContext.data,
          group: newGroup,
        } as Block,
      });
    }

    setIsEditingGroup(false);
  };

  const saveJsonData = () => {
    if (!testContext) return;
    try {
      const parsedData = JSON.parse(jsonData);
      updateContext({ data: parsedData });
      setIsJsonModified(false);
    } catch {
      enqueueSnackbar("Invalid JSON format", {
        variant: "error",
        autoHideDuration: 2000,
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    }
  };

  useEffect(() => {
    if (testContext?.data) {
      setJsonData(JSON.stringify(testContext.data, null, 2));
      setIsJsonModified(false);
    }
  }, [testContext?.data]);

  return (
    <Card
      key={testContext.id}
      sx={{
        overflowY: "auto",
        height: "calc(100vh - 310px)",
        borderRadius: "6px",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{ mb: 1.5 }}
        >
          {isEditingName ? (
            <Stack direction="row" spacing={0.5} alignItems="center" flex={1}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveField("name");
                  }
                }}
                sx={{ fontSize: "14px" }}
              />
              <IconButton
                aria-label="Save name"
                color="primary"
                onClick={() => saveField("name")}
                disabled={isUpdating}
                size="small"
                sx={{ p: 0.5 }}
              >
                <SaveIcon sx={{ fontSize: "18px" }} />
              </IconButton>
              <IconButton
                aria-label="Cancel editing name"
                onClick={() => cancelEditing("name")}
                size="small"
                sx={{ p: 0.5 }}
              >
                <CancelIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Stack>
          ) : (
            <Typography
              variant="h6"
              sx={{
                flex: 1,
                fontSize: "16px",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { color: "primary.main" },
              }}
              {...clickableProps(() => startEditing("name"))}
              onClick={() => startEditing("name")}
            >
              {testContext.name}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {testContext?.data?.should_fix_with_ai && (
              <Chip
                label="AI Fix"
                color="warning"
                size="small"
                sx={{ height: "20px", fontSize: "10px" }}
              />
            )}
            {testContext?.data?.should_try_deterministic && (
              <Chip
                label="Det. Fix"
                color="info"
                size="small"
                sx={{ height: "20px", fontSize: "10px" }}
              />
            )}
            {testContextStep && (
              <Chip
                label={humanizeText(testContextStep.run_status)}
                color={getRunStatusColor(testContextStep.run_status)}
                size="small"
                sx={{ height: "20px", fontSize: "10px" }}
              />
            )}
          </Stack>
        </Stack>

        {isEditingDescription ? (
          <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={2}
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              sx={{ fontSize: "13px" }}
            />
            <IconButton
              aria-label="Save description"
              color="primary"
              onClick={() => saveField("description")}
              disabled={isUpdating}
              size="small"
              sx={{ p: 0.5 }}
            >
              <SaveIcon sx={{ fontSize: "18px" }} />
            </IconButton>
            <IconButton
              aria-label="Cancel editing description"
              onClick={() => cancelEditing("description")}
              size="small"
              sx={{ p: 0.5 }}
            >
              <CancelIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              cursor: "pointer",
              mb: 1,
              "&:hover": { color: "text.primary" },
            }}
            {...clickableProps(() => startEditing("description"))}
            onClick={() => startEditing("description")}
          >
            {testContext.description ?? "No description"}
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              mb: 0.5,
              display: "block",
            }}
          >
            End goal
          </Typography>
          {isEditingEndGoal ? (
            <Stack direction="row" spacing={0.5} alignItems="center" width="100%">
              <TextField
                fullWidth
                placeholder="e.g. 'User should be able to login successfully'"
                variant="outlined"
                size="small"
                value={editedEndGoal}
                onChange={(e) => setEditedEndGoal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveField("endGoal");
                  }
                }}
                sx={{ fontSize: "13px" }}
              />
              <IconButton
                aria-label="Save end goal"
                color="primary"
                onClick={() => saveField("endGoal")}
                disabled={isUpdating}
                size="small"
                sx={{ p: 0.5 }}
              >
                <SaveIcon sx={{ fontSize: "18px" }} />
              </IconButton>
              <IconButton
                aria-label="Cancel editing end goal"
                onClick={() => cancelEditing("endGoal")}
                size="small"
                sx={{ p: 0.5 }}
              >
                <CancelIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                cursor: "pointer",
                "&:hover": { color: "primary.main" },
              }}
              {...clickableProps(() => startEditing("endGoal"))}
              onClick={() => startEditing("endGoal")}
            >
              {testContext.end_goal ?? "<No end goal>"}
            </Typography>
          )}
        </Box>

        {/* Group Assignment */}
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Group (Optional)
            </Typography>
            {isGroupCheck && (
              <Chip
                label="Group Check"
                size="small"
                color="primary"
                icon={<AccountTreeIcon />}
                sx={{ fontSize: "10px", height: "20px" }}
              />
            )}
          </Stack>

          {isEditingGroup ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Autocomplete
                freeSolo
                size="small"
                fullWidth
                options={existingGroups}
                value={editedGroup}
                onChange={(_, newValue) => setEditedGroup(newValue || "")}
                onInputChange={(_, newValue) => setEditedGroup(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Enter or select group name" size="small" />
                )}
              />
              <IconButton
                aria-label="Save group"
                color="primary"
                size="small"
                onClick={() => saveGroup()}
                disabled={isUpdating}
                sx={{ p: 0.5 }}
              >
                <SaveIcon sx={{ fontSize: "18px" }} />
              </IconButton>
              <IconButton
                aria-label="Cancel editing group"
                size="small"
                onClick={() => {
                  setIsEditingGroup(false);
                  setEditedGroup(testContext.data?.group || "");
                }}
                sx={{ p: 0.5 }}
              >
                <CancelIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              {...clickableProps(() => {
                setEditedGroup(testContext.data?.group || "");
                setIsEditingGroup(true);
              })}
              onClick={() => {
                setEditedGroup(testContext.data?.group || "");
                setIsEditingGroup(true);
              }}
              sx={{
                fontSize: "13px",
                cursor: "pointer",
                color: testContext.data?.group ? "text.primary" : "text.secondary",
                fontStyle: testContext.data?.group ? "normal" : "italic",
                "&:hover": { color: "primary.main" },
              }}
            >
              {testContext.data?.group || "No group (click to add)"}
            </Typography>
          )}

          {/* Info text about group checks */}
          {testContext.data?.group && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                mt: 0.5,
                display: "block",
              }}
            >
              {isGroupCheck
                ? "This is the group check step. If it fails, all steps in this group will be skipped."
                : "This step belongs to a group. It will be skipped if the group check fails."}
            </Typography>
          )}
        </Box>

        {testContext?.data && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                mb: 0.5,
                display: "block",
              }}
            >
              Reasoning ({testContext?.data.confidence ?? "unknown"} confidence)
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "13px", color: "text.secondary" }}>
              {testContext.data.reasoning ?? "No reasoning provided"}
            </Typography>
          </Box>
        )}

        {testContext.pre_conditions && testContext.pre_conditions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                mb: 0.5,
                display: "block",
              }}
            >
              Pre-conditions
            </Typography>
            <Stack spacing={0.5}>
              {testContext.pre_conditions.map((pre, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: "13px" }}>
                  • {pre.state_invariant}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {testContext.post_conditions && testContext.post_conditions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                mb: 0.5,
                display: "block",
              }}
            >
              Post-conditions
            </Typography>
            <Stack spacing={0.5}>
              {testContext.post_conditions.map((post, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: "13px" }}>
                  • {post.state_invariant}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {testContext.actions && testContext.actions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                mb: 0.5,
                display: "block",
              }}
            >
              Actions
            </Typography>
            <Stack spacing={0.5}>
              {testContext.actions.map((action, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: "13px" }}>
                  #{i + 1} {action.name}
                  {action.description ? ` - ${action.description}` : ""}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {useBlockEditor && testContext.data ? (
          <BlockEditor
            block={testContext.data}
            appConfigId={test?.app_config_id}
            onUpdate={(updatedBlock) => {
              updateContext({ data: updatedBlock });
            }}
          />
        ) : (
          <TabContext value={dataTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <TabList onChange={handleDataChange} aria-label="Test context tabs">
                <Tab label="Code" value="main" />
                <Tab label="Raw" value="data" />
              </TabList>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={debugParams.debug === "true"}
                    onChange={(e) => {
                      setDebugParam("debug", e.target.checked ? "true" : "false");
                    }}
                  />
                }
                label="Dev mode"
                sx={{ mr: 2 }}
              />
            </Box>
            <TabPanel value="main">
              {testContext.data && (
                <Box mt={2} borderRadius={1} border={1} borderColor="divider" p={2}>
                  <Editor
                    height="200px"
                    defaultLanguage="python"
                    value={testContext.data.code}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                    }}
                  />
                </Box>
              )}
            </TabPanel>
            <TabPanel value="data">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Editor
                  height="450px"
                  defaultLanguage="json"
                  value={jsonData}
                  onChange={(value) => {
                    setJsonData(value || "{}");
                    setIsJsonModified(true);
                  }}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveJsonData}
                    disabled={!isJsonModified || isUpdating}
                    startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            </TabPanel>
          </TabContext>
        )}

        {(testContext.start_details || testContext.end_details) && (
          <Stack
            direction="column"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            mt={2}
          >
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
              Screenshots
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              {testContext.start_details?.screenshot && (
                <ImageFromPath height={200} path={testContext.start_details.screenshot} />
              )}
              {testContext.end_details?.screenshot && (
                <ImageFromPath height={200} path={testContext.end_details.screenshot} />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              From{" "}
              {testContext.started_at ? new Date(testContext.started_at).toLocaleString() : "-"} to{" "}
              {testContext.ended_at ? new Date(testContext.ended_at).toLocaleString() : "-"}
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
